/**
 * 어시스트 빈 수납건 팀 지정 패널 (데일리 리포트 화면)
 *
 * 설계 합의(2026-09-03):
 *  · 그날 팀을 못 정한 수납건에 팀을 지정한다 — 관리자만. 그 건만 수동, 나머지는 자동 배분.
 *  · 의사 월 매핑(그 달 그 의사의 풀 전부를 한 팀으로 강제) — 관리자만, 그 달에만, 상속 없음.
 *  · 지정은 즉시 저장(salesAttribution/{yearMonth}), 실적 재계산은 "실적 반영" 버튼 한 번.
 *  · 현재 월이 아닌 달을 건드리면 확인 대화상자 + 활동 기록.
 *
 * 풀 판정은 서비스와 **같은 규칙**을 쓴다: 그 날짜 집계행(teamCollection)으로 해석기를 만들고
 * 어시스트 이름을 풀어 '미지정'으로 떨어지는 수납건만 표에 올린다. 어시스트 이름이 있어도
 * 명단·엑셀 팀칸으로 못 푸는 건(9/1 김은자 2,745,670원)은 풀이므로 여기 나와야 하고, 반대로
 * 업로드가 이미 팀을 박은 빈 행(6·7월)은 풀이 아니므로 나오면 안 된다.
 *
 * 환자명은 표시하지 않는다 — 차트번호·진료의사·금액이면 지정에 충분하다.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReceiptItem } from '../../types/dailyReport.ts';
import { useAuth } from '../../contexts/AuthContext.tsx';
import {
  loadSalesAttributionDoc,
  loadDailyTeamCollectionRows,
  loadMonthDoctors,
  setReceiptTeamOverride,
  setDoctorTeamMappingForMonth,
  markAttributionRefreshed,
} from '../../services/firebase/salesAttributionFirebaseService.ts';
import { attributionOverrideKey, buildDayAssistResolver, flattenAttributionDoc, type SalesAttributionDoc } from '../../utils/salesAttribution.ts';
import { buildEmployeeTeamMap, getTeamForAssist } from '../../services/teamMonthlyDataService.ts';
import { refreshMonthAfterAttributionChange } from '../../services/attributionRefreshService.ts';
import { userActivityLogService } from '../../services/userActivityLogService.ts';

interface Props {
  /** YYYY-MM-DD */
  date: string;
  /** 그날 수납건 (환자명 포함 — 이 컴포넌트는 표시하지 않는다) */
  receipts: ReceiptItem[];
  /** 그 달 팀 목록 ('미지정' 제외) */
  teams: string[];
  /** 팀 목록 원본(성과 재계산 입력) */
  teamObjects: any[];
  employees: any[];
  budgetData: any;
  setBudgetData: (data: any | ((prev: any) => any)) => void;
}

/** 한국 시간 기준 현재 년월 — UTC로 판정하면 매월 1일 새벽에 전월이 '현재 월'로 보인다 */
const seoulYearMonth = (): string => {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit' }).format(new Date());
  return parts.slice(0, 7);
};

interface PoolGroup {
  key: string;          // attributionOverrideKey(date, chartNumber)
  chartNumber: string;
  doctorName: string;
  amount: number;
  count: number;
  assistNames: string[];
}

const BlankAssistAttributionPanel: React.FC<Props> = ({ date, receipts, teams, teamObjects, employees, budgetData, setBudgetData }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const yearMonth = date.slice(0, 7);

  const [attrDoc, setAttrDoc] = useState<SalesAttributionDoc | null>(null);
  const [dayRows, setDayRows] = useState<any[] | null>(null);
  const [monthDoctors, setMonthDoctors] = useState<string[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const [doc, rows] = await Promise.all([loadSalesAttributionDoc(yearMonth), loadDailyTeamCollectionRows(date)]);
      setAttrDoc(doc);
      setDayRows(rows);
      setError(null);
    } catch (e: any) {
      setError(`지정 정보를 불러오지 못했습니다: ${e?.message || e}. 이 상태로는 지정 현황이 정확하지 않습니다.`);
    }
  }, [yearMonth, date]);

  useEffect(() => { reload(); }, [reload]);

  // 의사 월 매핑은 월 단위 결정이므로 목록도 그 달 전체에서 모은다. 월이 바뀔 때만 읽는다.
  useEffect(() => {
    let cancelled = false;
    loadMonthDoctors(yearMonth)
      .then(list => { if (!cancelled) setMonthDoctors(list); })
      .catch(() => { if (!cancelled) setMonthDoctors([]); });
    return () => { cancelled = true; };
  }, [yearMonth]);

  const { receiptOverrides, doctorTeamMapping } = useMemo(() => flattenAttributionDoc(attrDoc), [attrDoc]);

  /** 서비스와 같은 해석 사슬로 그날 풀을 판정하고, 같은 차트번호는 한 묶음으로 (지정 키 단위와 일치) */
  const poolGroups = useMemo<PoolGroup[]>(() => {
    if (!dayRows) return [];
    const employeeTeamMap = buildEmployeeTeamMap(employees || []);
    const resolver = buildDayAssistResolver(dayRows, (row: any, nameFromKey: string) =>
      getTeamForAssist(nameFromKey, employeeTeamMap, row.cashierName || '', undefined, undefined, row.team)
    );
    const byKey: Record<string, PoolGroup> = {};
    (receipts || []).forEach(r => {
      const assistName = (r.assistName ?? '').toString().trim();
      const doctorName = (r.doctorName ?? '').toString().trim();
      const amount = Number(r.amount) || 0;
      if (resolver(assistName, date) !== '미지정') return;   // 팀이 풀렸거나(직접 귀속) 집계행이 없음
      if (amount === 0 && !doctorName) return;                // 자리표시 행
      const chartNumber = String(r.chartNumber ?? '');
      const key = attributionOverrideKey(date, chartNumber);
      const g = (byKey[key] = byKey[key] || { key, chartNumber, doctorName, amount: 0, count: 0, assistNames: [] });
      g.amount += amount;
      g.count += 1;
      if (!g.doctorName) g.doctorName = doctorName;
      const shown = assistName || '(빈칸)';
      if (!g.assistNames.includes(shown)) g.assistNames.push(shown);
    });
    return Object.values(byKey).sort((a, b) => b.amount - a.amount);
  }, [dayRows, receipts, employees, date]);

  const doctorsInMonth = useMemo(
    () => Array.from(new Set([
      ...monthDoctors,
      ...Object.keys(doctorTeamMapping),
      ...(receipts || []).map(r => (r.doctorName || '').trim()),
    ].filter(Boolean))).sort(),
    [monthDoctors, doctorTeamMapping, receipts]
  );

  const loading = dayRows === null;
  // 그날 풀이 비어도 의사 월 매핑은 손댈 수 있어야 한다 (매핑은 월 단위 결정이다)
  if (!loading && poolGroups.length === 0 && doctorsInMonth.length === 0 && !attrDoc?.pendingRefresh && !error) return null;

  /** 현재 월(서울 기준)이 아니면 확인 — 이미 지급한 달의 실적이 바뀐다 */
  const confirmIfPastMonth = (what: string): boolean => {
    if (yearMonth === seoulYearMonth()) return true;
    return window.confirm(
      `${yearMonth}은 현재 월이 아닙니다.\n${what}으로 이미 지급했을 수 있는 ${yearMonth} 팀 실적과 인센티브 계산이 바뀝니다.\n\n계속하시겠습니까?`
    );
  };

  const actor = { uid: user?.uid || '', email: user?.email || null };
  /** 활동 기록은 삼키지 않는다 — 금액 귀속 변경이 무기록으로 남으면 안 된다 */
  const log = async (action: string, details: string) => {
    try {
      await userActivityLogService.logActivity({
        userId: user?.uid || '', userEmail: user?.email || '', userName: user?.displayName || undefined, action, details,
      } as any);
      return true;
    } catch {
      return false;
    }
  };

  const afterWrite = async (action: string, details: string) => {
    const logged = await log(action, details);
    await reload();
    setNotice(logged ? null : '저장은 됐지만 변경 기록 남기기에 실패했습니다. 무엇을 바꿨는지 따로 적어 두세요.');
  };

  const assignReceipt = async (g: PoolGroup, team: string) => {
    if (!isAdmin) return;
    if (!confirmIfPastMonth(`수납건 ${g.chartNumber}의 팀 지정`)) return;
    setBusy(g.key);
    try {
      await setReceiptTeamOverride(yearMonth, date, g.chartNumber, team, actor);
      await afterWrite('수납건 팀 지정', `${date} 차트 ${g.chartNumber}${g.count > 1 ? ` (${g.count}건 묶음)` : ''} (${g.doctorName || '의사 없음'}, ${g.amount.toLocaleString()}원) → ${team || '지정 해제'}`);
    } catch (e: any) {
      setError(`저장 실패: ${e?.message || e}`);
    } finally {
      setBusy(null);
    }
  };

  const assignDoctor = async (doctor: string, team: string) => {
    if (!isAdmin) return;
    if (!confirmIfPastMonth(`진료의사 ${doctor}의 월 매핑 변경`)) return;
    setBusy(`doctor|${doctor}`);
    try {
      await setDoctorTeamMappingForMonth(yearMonth, doctor, team, actor);
      await afterWrite('진료의사 월 매핑', `${yearMonth} ${doctor} → ${team || '매핑 해제'} (그 달 팀 미정 진료 전부)`);
    } catch (e: any) {
      setError(`저장 실패: ${e?.message || e}`);
    } finally {
      setBusy(null);
    }
  };

  const refresh = async () => {
    if (!isAdmin) return;   // 실적 반영도 관리자만 — 실제매출 캐시·팀 성과 저장을 유발한다
    if (!confirmIfPastMonth('실적 반영')) return;
    setRefreshing(true);
    setNotice(null);
    try {
      const r = await refreshMonthAfterAttributionChange({ yearMonth, teams: teamObjects || [], employees: employees || [], budgetData, setBudgetData });
      // 실제로 저장까지 됐을 때만 '반영 안 됨' 표시를 끈다 — 실패했는데 끄면 반영된 것처럼 보인다
      if (r.actualSalesPersisted && r.performancePersisted) {
        await markAttributionRefreshed(yearMonth);
        setNotice(`${yearMonth} 실적 반영 완료 · ${r.message}`);
      } else {
        setError(`실적이 반영되지 않았습니다 — ${r.message}`);
      }
      await reload();
    } catch (e: any) {
      setError(`실적 반영 실패: ${e?.message || e}`);
    } finally {
      setRefreshing(false);
    }
  };

  const selectClass = (enabled: boolean) =>
    `border rounded-md px-2 py-1 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 ${enabled ? 'bg-white border-gray-300' : 'bg-gray-100 text-gray-600 cursor-not-allowed'}`;
  const lockTitle = isAdmin ? undefined : '팀 지정은 관리자만 할 수 있습니다';

  return (
    <section className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-4" aria-label="팀 미정 수납건 지정">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900">팀을 정하지 못한 수납건 · {date}</h3>
          <p className="text-sm text-gray-600 mt-1">
            지정하지 않으면 그날 진료의사의 팀이 정해진 진료 비율로 <b>자동 배분</b>됩니다. 지정한 건만 수동으로 바뀝니다.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {attrDoc?.pendingRefresh && (
            <span className="text-sm font-medium text-orange-800 bg-orange-100 border border-orange-200 rounded-full px-3 py-1">
              지정 변경이 실적에 아직 반영되지 않았습니다
            </span>
          )}
          <button
            type="button"
            onClick={refresh}
            disabled={!isAdmin || refreshing}
            title={isAdmin ? '이 달 실제매출과 팀 성과를 다시 계산합니다' : '실적 반영은 관리자만 할 수 있습니다'}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg px-4 py-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
            aria-disabled={!isAdmin || refreshing}
          >
            {refreshing ? '반영 중…' : '실적 반영'}
          </button>
        </div>
      </div>

      {error && <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">{error}</div>}
      {notice && <div className="text-sm text-blue-900 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-3">{notice}</div>}
      {loading && <div className="text-sm text-gray-600">집계행을 읽는 중…</div>}

      {!loading && poolGroups.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-gray-600 border-b">
                <th className="text-left py-1 pr-3 font-medium">차트번호</th>
                <th className="text-left py-1 pr-3 font-medium">어시스트</th>
                <th className="text-left py-1 pr-3 font-medium">진료의사</th>
                <th className="text-right py-1 pr-3 font-medium">금액</th>
                <th className="text-left py-1 font-medium">팀 지정</th>
              </tr>
            </thead>
            <tbody>
              {poolGroups.map(g => {
                const current = receiptOverrides[g.key] || '';
                return (
                  <tr key={g.key} className="border-b border-gray-100">
                    <td className="py-1 pr-3 tabular-nums text-gray-900">
                      {g.chartNumber}
                      {g.count > 1 && <span className="ml-1 text-xs font-medium text-gray-600">{g.count}건 묶음</span>}
                    </td>
                    <td className="py-1 pr-3 text-gray-700">{g.assistNames.join(', ')}</td>
                    <td className="py-1 pr-3 text-gray-700">{g.doctorName || <span className="text-red-700">의사 없음</span>}</td>
                    <td className="py-1 pr-3 text-right tabular-nums text-gray-900">{g.amount.toLocaleString()}원</td>
                    <td className="py-1">
                      <select
                        value={current}
                        disabled={!isAdmin || busy === g.key}
                        title={lockTitle}
                        aria-label={`차트 ${g.chartNumber} 팀 지정`}
                        className={selectClass(isAdmin)}
                        onChange={e => assignReceipt(g, e.target.value)}
                      >
                        <option value="">자동 배분</option>
                        {teams.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      {current && attrDoc?.receiptOverrides?.[g.key]?.by && (
                        <span className="ml-2 text-xs font-medium text-gray-600">{attrDoc.receiptOverrides[g.key].by}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && poolGroups.length === 0 && (
        <div className="text-sm text-gray-600">이 날짜에 팀을 정하지 못한 수납건은 없습니다.</div>
      )}

      {doctorsInMonth.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="text-sm font-medium text-gray-800 mb-1">진료의사 월 매핑 · {yearMonth} <span className="font-normal text-gray-600">(이 달 진료한 의사 {doctorsInMonth.length}명)</span></div>
          <p className="text-xs text-gray-600 mb-2">
            지정하면 <b>이 달 그 의사의 팀 미정 진료 전부</b>가 그 팀으로 강제됩니다(자동 배분 무시). 이 달에만 적용되고 다음 달로 넘어가지 않습니다.
          </p>
          <div className="flex flex-wrap gap-3">
            {doctorsInMonth.map(doc => {
              const current = doctorTeamMapping[doc] || '';
              const key = `doctor|${doc}`;
              return (
                <label key={doc} className="flex items-center gap-2 text-sm">
                  <span className="text-gray-800">{doc}</span>
                  <select
                    value={current}
                    disabled={!isAdmin || busy === key}
                    title={lockTitle}
                    aria-label={`진료의사 ${doc} 월 매핑`}
                    className={selectClass(isAdmin)}
                    onChange={e => assignDoctor(doc, e.target.value)}
                  >
                    <option value="">자동 배분</option>
                    {teams.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};

export default BlankAssistAttributionPanel;
